import { createClient } from '@supabase/supabase-js';
import { Project, CommunityProject } from '@/types';
import html2canvas from 'html2canvas';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export async function validatePassword(password: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('passwords')
      .select('*')
      .eq('password', password)
      .eq('is_active', true)
      .gte('expires_at', new Date().toISOString())
      .single();

    if (error) {
      console.error('Error validating password:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error validating password:', error);
    return false;
  }
}

export async function publishProject(project: Project): Promise<void> {
  try {
    // Validate project data
    if (!project.settings.name || !project.settings.author || !project.settings.nickname) {
      throw new Error('Missing required project information');
    }

    if (!project.components.length) {
      throw new Error('Cannot publish an empty project');
    }

    // Use a default preview image
    const previewUrl = 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200&h=800&fit=crop';

    const { error } = await supabase
      .from('community_projects')
      .insert({
        name: project.settings.name,
        author: project.settings.author,
        nickname: project.settings.nickname,
        preview_url: previewUrl,
        components: project.components,
        settings: project.settings,
      });

    if (error) {
      console.error('Supabase insert error:', error);
      throw new Error(error.message);
    }
  } catch (error) {
    console.error('Error publishing project:', error);
    throw error;
  }
}

export async function getCommunityProjects(): Promise<CommunityProject[]> {
  try {
    const { data, error } = await supabase
      .from('community_projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase select error:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching community projects:', error);
    throw error;
  }
}

export async function incrementDownloads(projectId: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('increment_downloads', { project_id: projectId });
    
    if (error) {
      console.error('Supabase RPC error:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error incrementing downloads:', error);
    throw error;
  }
}